<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { convertToPipelineSlug } from 'dashboard/helper/commons.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['close']);

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const name = ref('');
const pipelineKey = ref('');
const description = ref('');
const isCreating = ref(false);

watch(name, newName => {
  pipelineKey.value = convertToPipelineSlug(newName);
});

const isFormValid = computed(() => {
  return name.value.trim().length > 0;
});

const createPipeline = async () => {
  if (!isFormValid.value) return;

  isCreating.value = true;
  try {
    const pipeline = await store.dispatch('pipelines/create', {
      name: name.value.trim(),
      description: description.value.trim(),
      key: pipelineKey.value,
    });
    useAlert(t('PIPELINES.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
    router.push({
      name: 'pipeline_edit',
      params: { pipelineId: pipeline.id },
    });
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('PIPELINES.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="$t('PIPELINES.ADD.TITLE')" />
    <form
      class="flex flex-col w-full gap-4 p-8"
      @submit.prevent="createPipeline"
    >
      <Input
        v-model="name"
        :label="$t('PIPELINES.FORM.NAME.LABEL')"
        :placeholder="$t('PIPELINES.FORM.NAME.PLACEHOLDER')"
        required
      />
      <Input
        v-model="pipelineKey"
        :label="$t('PIPELINES.FORM.KEY.LABEL')"
        :help-text="$t('PIPELINES.FORM.KEY.HELP_TEXT')"
        readonly
      />
      <Textarea
        v-model="description"
        :label="$t('PIPELINES.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('PIPELINES.FORM.DESCRIPTION.PLACEHOLDER')"
        :rows="3"
      />
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          slate
          :label="$t('PIPELINES.FORM.CANCEL')"
          @click="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('PIPELINES.FORM.CREATE')"
          :is-loading="isCreating"
          :disabled="!isFormValid"
        />
      </div>
    </form>
  </div>
</template>
